import { useEffect, useState } from "react";
import { Save, MapPin, Radar, Clock, Building2, Phone, Store } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ProfilePage() {
  return (
    <DashboardShell active="profile">
      {({ merchant, setMerchant }) => <ProfileBody merchant={merchant} setMerchant={setMerchant} />}
    </DashboardShell>
  );
}

function ProfileBody({ merchant, setMerchant }) {
  const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];
  const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

  const [form, setForm] = useState({
    business_name: merchant.business_name || "",
    category: merchant.category || "general",
    description: merchant.description || "",
    address: merchant.address || "",
    lat: merchant.lat || "",
    lng: merchant.lng || "",
    radar_days: merchant.radar_days ?? [0, 1, 2, 3, 4, 5, 6],
    radar_start: merchant.radar_start || "09:00",
    radar_end: merchant.radar_end || "22:00",
    radar_radius_m: merchant.radar_radius_m || 50,
    // Extended fields
    street: merchant.street || "",
    ext_number: merchant.ext_number || "",
    int_number: merchant.int_number || "",
    colonia: merchant.colonia || "",
    municipio: merchant.municipio || "",
    estado: merchant.estado || "",
    postal_code: merchant.postal_code || "",
    contact_phone: merchant.contact_phone || "",
    plaza_name: merchant.plaza_name || "",
    business_hours: merchant.business_hours || "",
    giro: merchant.giro || "",
  });
  const [saving, setSaving] = useState(false);
  const [savingCoords, setSavingCoords] = useState(false);

  useEffect(() => {
    setForm({
      business_name: merchant.business_name || "",
      category: merchant.category || "general",
      description: merchant.description || "",
      address: merchant.address || "",
      lat: merchant.lat ?? "",
      lng: merchant.lng ?? "",
      radar_days: merchant.radar_days ?? [0, 1, 2, 3, 4, 5, 6],
      radar_start: merchant.radar_start || "09:00",
      radar_end: merchant.radar_end || "22:00",
      radar_radius_m: merchant.radar_radius_m || 50,
      street: merchant.street || "",
      ext_number: merchant.ext_number || "",
      int_number: merchant.int_number || "",
      colonia: merchant.colonia || "",
      municipio: merchant.municipio || "",
      estado: merchant.estado || "",
      postal_code: merchant.postal_code || "",
      contact_phone: merchant.contact_phone || "",
      plaza_name: merchant.plaza_name || "",
      business_hours: merchant.business_hours || "",
      giro: merchant.giro || "",
    });
  }, [merchant]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (d) => {
    setForm((f) => {
      const arr = f.radar_days || [];
      return {
        ...f,
        radar_days: arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d].sort(),
      };
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta GPS");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        upd("lat", pos.coords.latitude);
        upd("lng", pos.coords.longitude);
        toast.success("Ubicación capturada");
      },
      () => toast.error("No pudimos obtener tu GPS"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const saveCoords = async () => {
    const latNum = form.lat === "" ? null : parseFloat(form.lat);
    const lngNum = form.lng === "" ? null : parseFloat(form.lng);
    if (latNum === null || lngNum === null || Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      toast.error("Latitud y longitud son obligatorias");
      return;
    }
    if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
      toast.error("Coordenadas fuera de rango válido");
      return;
    }
    setSavingCoords(true);
    try {
      const { data } = await api.put("/merchants/me", { lat: latNum, lng: lngNum });
      setMerchant(data);
      toast.success("Coordenadas actualizadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos guardar las coordenadas");
    } finally {
      setSavingCoords(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name,
        category: form.category,
        description: form.description,
        address: form.address,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
        radar_days: form.radar_days,
        radar_start: form.radar_start,
        radar_end: form.radar_end,
        radar_radius_m: form.radar_radius_m,
        street: form.street,
        ext_number: form.ext_number,
        int_number: form.int_number,
        colonia: form.colonia,
        municipio: form.municipio,
        estado: form.estado,
        postal_code: form.postal_code,
        contact_phone: form.contact_phone,
        plaza_name: form.plaza_name,
        business_hours: form.business_hours,
        giro: form.giro,
      };
      const { data } = await api.put("/merchants/me", payload);
      setMerchant(data);
      toast.success("Comercio actualizado");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Configuración</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Mi comercio</h1>
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 p-6 space-y-4">
        <div>
          <Label>Nombre del negocio</Label>
          <Input value={form.business_name} onChange={(e) => upd("business_name", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="profile-name" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Categoría</Label>
            <select value={form.category} onChange={(e) => upd("category", e.target.value)} className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm bg-white">
              <option value="cafeteria">Cafetería</option>
              <option value="restaurante">Restaurante</option>
              <option value="ropa">Ropa</option>
              <option value="belleza">Belleza</option>
              <option value="tecnologia">Tecnología</option>
              <option value="general">Otro</option>
            </select>
          </div>
          <div>
            <Label>Dirección visible</Label>
            <Input value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Calle 10 #45-67" className="mt-1.5 h-11 rounded-xl" />
          </div>
        </div>

        <div>
          <Label>Descripción</Label>
          <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3} placeholder="Cuenta a tus clientes qué te hace único" className="mt-1.5 rounded-xl" />
        </div>
      </div>

      {/* Domicilio completo */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-heading font-extrabold text-lg">Domicilio del negocio</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">
          Dirección física completa. Se usa para facturación, verificación y para que los usuarios sepan dónde llegar.
        </p>
        <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-4">
          <div>
            <Label>Calle</Label>
            <Input value={form.street} onChange={(e) => upd("street", e.target.value)} placeholder="Av. Vallarta" className="mt-1.5 h-11 rounded-xl" data-testid="profile-street" />
          </div>
          <div>
            <Label>Número exterior</Label>
            <Input value={form.ext_number} onChange={(e) => upd("ext_number", e.target.value)} placeholder="1234" className="mt-1.5 h-11 rounded-xl" data-testid="profile-ext-number" />
          </div>
          <div>
            <Label>Número interior <span className="text-neutral-400">(opcional)</span></Label>
            <Input value={form.int_number} onChange={(e) => upd("int_number", e.target.value)} placeholder="Local 12A" className="mt-1.5 h-11 rounded-xl" data-testid="profile-int-number" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Colonia</Label>
            <Input value={form.colonia} onChange={(e) => upd("colonia", e.target.value)} placeholder="Providencia" className="mt-1.5 h-11 rounded-xl" data-testid="profile-colonia" />
          </div>
          <div>
            <Label>Municipio / Delegación</Label>
            <Input value={form.municipio} onChange={(e) => upd("municipio", e.target.value)} placeholder="Guadalajara" className="mt-1.5 h-11 rounded-xl" data-testid="profile-municipio" />
          </div>
        </div>
        <div className="grid md:grid-cols-[1fr_1fr] gap-4 mt-4">
          <div>
            <Label>Estado</Label>
            <Input value={form.estado} onChange={(e) => upd("estado", e.target.value)} placeholder="Jalisco" className="mt-1.5 h-11 rounded-xl" data-testid="profile-estado" />
          </div>
          <div>
            <Label>C.P.</Label>
            <Input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} placeholder="44630" maxLength={5} className="mt-1.5 h-11 rounded-xl" data-testid="profile-postal-code" />
          </div>
        </div>
        <div className="mt-4">
          <Label>Nombre de la plaza o centro comercial <span className="text-neutral-400">(opcional)</span></Label>
          <Input value={form.plaza_name} onChange={(e) => upd("plaza_name", e.target.value)} placeholder="Plaza Andares · Local 118" className="mt-1.5 h-11 rounded-xl" data-testid="profile-plaza-name" />
        </div>
      </div>

      {/* Contacto y giro */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Phone className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-heading font-extrabold text-lg">Contacto y giro</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Teléfono de contacto</Label>
            <Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} placeholder="+52 33 1234 5678" type="tel" className="mt-1.5 h-11 rounded-xl" data-testid="profile-contact-phone" />
          </div>
          <div>
            <Label>Giro principal</Label>
            <div className="relative mt-1.5">
              <Store className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input value={form.giro} onChange={(e) => upd("giro", e.target.value)} placeholder="Ropa boutique para dama" className="h-11 rounded-xl pl-9" data-testid="profile-giro" />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Label>Horarios de atención</Label>
          <Input value={form.business_hours} onChange={(e) => upd("business_hours", e.target.value)} placeholder="Lun-Vie 10:00-21:00 · Sáb 11:00-22:00 · Dom cerrado" className="mt-1.5 h-11 rounded-xl" data-testid="profile-business-hours" />
          <p className="text-xs text-neutral-500 mt-1.5">Este es el horario que verá el usuario. El horario de radar activo (más abajo) puede ser diferente.</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border-2 border-[#0055FF]/30 p-6 shadow-sm" data-testid="new-coords-card">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-heading font-extrabold text-lg tracking-wide">NUEVAS COORDENADAS DEL NEGOCIO</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">
          Rectifica o actualiza el punto exacto en el mapa donde está tu comercio. El alcance del radar (a cuántos metros se dispara el match) lo configuras abajo.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Latitud</Label>
            <Input value={form.lat} onChange={(e) => upd("lat", e.target.value)} placeholder="19.4326" className="mt-1.5 h-11 rounded-xl" data-testid="profile-lat" />
          </div>
          <div>
            <Label>Longitud</Label>
            <Input value={form.lng} onChange={(e) => upd("lng", e.target.value)} placeholder="-99.1332" className="mt-1.5 h-11 rounded-xl" data-testid="profile-lng" />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button onClick={useMyLocation} variant="outline" className="rounded-full" data-testid="use-my-gps">
            <Radar className="w-4 h-4" /> Usar mi ubicación actual
          </Button>
          <Button
            onClick={saveCoords}
            disabled={savingCoords}
            className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold tracking-wide"
            data-testid="save-coords-btn"
          >
            <Save className="w-4 h-4" /> {savingCoords ? "GUARDANDO…" : "GUARDAR COORDENADAS"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 p-6" data-testid="profile-radar-radius-card">
        <div className="flex items-center gap-2 mb-2">
          <Radar className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-heading font-extrabold text-lg">Alcance del radar</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">
          ¿A cuántos metros de tu comercio quieres que se dispare el match? Elige según cómo llegan tus clientes.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { v: 50, icon: "🎯", label: "Vitrina", tag: "50 m", desc: "Peatones frente al aparador. Ideal para tiendas de barrio y locales pequeños." },
            { v: 200, icon: "🏬", label: "Plaza", tag: "200 m", desc: "Clientes dentro de un centro comercial, otra tienda o estacionamiento. Recomendado para plazas." },
            { v: 500, icon: "🚗", label: "Vial", tag: "500 m", desc: "Autos en movimiento, drive-thrus, gasolineras y comercios sobre avenida." },
          ].map((opt) => {
            const active = Number(form.radar_radius_m) === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => upd("radar_radius_m", opt.v)}
                className={`text-left rounded-2xl border-2 p-4 transition-all ${
                  active
                    ? "border-[#0055FF] bg-[#0055FF]/5 shadow-md"
                    : "border-neutral-200 bg-white hover:border-neutral-300"
                }`}
                data-testid={`radius-option-${opt.v}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{opt.icon}</span>
                  <div className="flex-1">
                    <div className="font-heading font-extrabold text-base">{opt.label}</div>
                    <div className={`text-xs font-bold ${active ? "text-[#0055FF]" : "text-neutral-500"}`}>{opt.tag}</div>
                  </div>
                  {active && (
                    <div className="w-6 h-6 rounded-full bg-[#0055FF] text-white grid place-items-center text-xs font-bold">✓</div>
                  )}
                </div>
                <p className="text-xs text-neutral-600 mt-2 leading-snug">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-heading font-extrabold text-lg">Horario de activación del radar</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">
          Elige los días y horas en que quieres que tu comercio aparezca en el radar de los transeúntes. Fuera de este horario nadie recibirá tus ofertas.
        </p>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {DAY_LABELS.map((lbl, i) => {
            const selected = form.radar_days?.includes(i);
            return (
              <button
                key={`day-${lbl}`}
                type="button"
                onClick={() => toggleDay(i)}
                title={DAY_NAMES[i]}
                data-testid={`radar-day-${i}`}
                className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
                  selected ? "bg-[#0055FF] text-white" : "bg-white border border-neutral-200 text-neutral-500 hover:bg-neutral-100"
                }`}
              >
                {lbl}
              </button>
            );
          })}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Abre a las</Label>
            <Input
              type="time"
              value={form.radar_start}
              onChange={(e) => upd("radar_start", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
              data-testid="radar-start-input"
            />
          </div>
          <div>
            <Label>Cierra a las</Label>
            <Input
              type="time"
              value={form.radar_end}
              onChange={(e) => upd("radar_end", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
              data-testid="radar-end-input"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold h-12 px-8" data-testid="save-profile-btn">
          <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}
